import { useSyncExternalStore } from "react";

/**
 * Installing the app on a device. Chromium browsers (Android, desktop Chrome and Edge) can offer
 * an install dialog the app opens from a button. They stop offering it for a while after someone
 * closes it, and Safari never does, so those get the steps for their browser instead.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const askedKey = "expenses.install-asked-at";
const DayMs = 24 * 60 * 60 * 1000;

let offer: BeforeInstallPromptEvent | undefined;
let installedNow = false;
const listeners = new Set<() => void>();

function changed() {
  for (const listener of listeners) listener();
}

/** Called once at start-up, before React renders: the browser can offer installing before any component is listening. */
export function watchInstallOffer(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    offer = event as BeforeInstallPromptEvent;
    changed();
  });
  window.addEventListener("appinstalled", () => {
    offer = undefined;
    installedNow = true;
    changed();
  });
  try {
    // From when the prompt counted opens rather than days.
    for (const old of [
      "expenses.opens",
      "expenses.last-seen-at",
      "expenses.install-closed-on-open",
    ])
      window.localStorage.removeItem(old);
  } catch {
    // ignore
  }
}

/**
 * Whether to ask now: the first time on this device, then again once `everyDays` have passed
 * since it last asked. `everyDays` of zero means only the first time.
 */
export function shouldAsk(lastAskedAt: number | null, now: number, everyDays: number): boolean {
  if (lastAskedAt === null) return true;
  if (everyDays <= 0) return false;
  return now - lastAskedAt >= everyDays * DayMs;
}

export function lastAsked(): number | null {
  try {
    const value = window.localStorage.getItem(askedKey);
    return value === null ? null : Number(value);
  } catch {
    // Storage blocked: never remembered, so treated as asked already rather than asked every time.
    return Date.now();
  }
}

export function markAsked(): void {
  try {
    window.localStorage.setItem(askedKey, String(Date.now()));
  } catch {
    // ignore
  }
}

/** Running as the installed app rather than in a browser tab. */
export function isInstalled(win: Window = window): boolean {
  return (
    win.matchMedia("(display-mode: standalone)").matches ||
    (win.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** An iPhone or iPad. iPadOS says it is a Mac, so a Mac with a touch screen counts too. */
export function isAppleMobile(
  nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator,
): boolean {
  return (
    /iPhone|iPad|iPod/.test(nav.userAgent) ||
    (/Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1)
  );
}

/** A phone or tablet: an iPhone, iPad or Android device. */
export function isMobileDevice(
  nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator,
): boolean {
  return isAppleMobile(nav) || /Android/.test(nav.userAgent);
}

/** An iPad, whose Safari puts Share at the top right rather than the bottom. */
export function isIPad(nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator): boolean {
  return /iPad/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1);
}

/** Which browser an iPhone or iPad is using; each keeps Share somewhere else. */
export type IosBrowser = "safari" | "chrome" | "firefox" | "edge";

export function iosBrowser(nav: Pick<Navigator, "userAgent"> = navigator): IosBrowser {
  const ua = nav.userAgent;
  if (/CriOS/.test(ua)) return "chrome";
  if (/FxiOS/.test(ua)) return "firefox";
  if (/EdgiOS/.test(ua)) return "edge";
  return "safari";
}

/**
 * Where the pop-up's arrow points, at the browser's button that starts adding to the Home Screen:
 * Safari's Share in the middle of its toolbar at the bottom, Firefox's Share at the left of its address bar at the
 * bottom, Chrome's Share in the address bar at the top right, and Share at the top on an iPad.
 * Elsewhere, no arrow.
 */
export type InstallPointer = "bottom" | "bottom-left" | "top-right" | "top" | "none";

export function installPointer(
  nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator,
): InstallPointer {
  if (!isAppleMobile(nav)) return "none";
  if (isIPad(nav)) return "top";
  switch (iosBrowser(nav)) {
    case "safari":
      return "bottom";
    case "firefox":
      return "bottom-left";
    case "chrome":
      return "top-right";
    default:
      return "none";
  }
}

/** A browser that can install the app from its own menu, when it isn't offering the dialog. */
export type InstallBrowser = "edge" | "chrome" | "android" | "mac-safari";

export function installBrowser(
  nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator,
): InstallBrowser | null {
  const ua = nav.userAgent;
  if (isAppleMobile(nav)) return null;
  if (/Android/.test(ua)) return /Chrome|EdgA|SamsungBrowser/.test(ua) ? "android" : null;
  if (/Edg\//.test(ua)) return "edge";
  if (/Chrome\//.test(ua) && !/OPR\/|Firefox/.test(ua)) return "chrome";
  if (/Macintosh/.test(ua) && /Version\/\d+.*Safari/.test(ua)) return "mac-safari";
  return null;
}

export type InstallOffer =
  | { kind: "button"; install: () => Promise<void> }
  | { kind: "ios" }
  | { kind: "browser"; browser: InstallBrowser }
  | null;

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** How this device can install the app, or null when it is installed or can't be. */
export function useInstallOffer(): InstallOffer {
  const current = useSyncExternalStore(subscribe, () => offer);
  if (installedNow || isInstalled()) return null;
  if (current) {
    return {
      kind: "button",
      install: async () => {
        await current.prompt();
        await current.userChoice;
        offer = undefined;
        changed();
      },
    };
  }
  if (isAppleMobile()) return { kind: "ios" };
  const browser = installBrowser();
  // On Android, Chrome offers its own Install dialog when it can; when it doesn't, the app is
  // usually installed already, and the menu steps don't apply.
  return browser && browser !== "android" ? { kind: "browser", browser } : null;
}
